import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDateTime } from './timezone';

// ─── Tiny inline bar chart (SVG, no external deps) ──────────────────────────

function TrendChart({ trend, height = 80 }) {
  if (!trend || trend.length === 0) return null;

  const maxTotal = Math.max(...trend.map(d => d.total), 1);
  const barWidth = Math.floor(560 / trend.length) - 2;

  return (
    <svg width="100%" viewBox={`0 0 560 ${height + 24}`} style={{ overflow: 'visible' }}>
      {trend.map((day, i) => {
        const successH = Math.round((day.success / maxTotal) * height);
        const failedH  = Math.round((day.failed  / maxTotal) * height);
        const x = i * (barWidth + 2);
        return (
          <g key={day.date}>
            {/* success bar */}
            <rect
              x={x}
              y={height - successH}
              width={barWidth}
              height={successH}
              fill="#22c55e"
              opacity={0.8}
            />
            {/* failure bar stacked on top */}
            <rect
              x={x}
              y={height - successH - failedH}
              width={barWidth}
              height={failedH}
              fill="#ef4444"
              opacity={0.85}
            />
          </g>
        );
      })}
      {/* X-axis labels — first, middle, last */}
      {[0, Math.floor(trend.length / 2), trend.length - 1].map(i => {
        const day = trend[i];
        if (!day) return null;
        const x = i * (barWidth + 2) + barWidth / 2;
        return (
          <text key={day.date} x={x} y={height + 18} fontSize="9" fill="var(--text-secondary)" textAnchor="middle">
            {day.date.slice(5)}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Metric card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-color)',
      borderRadius: 8,
      padding: '14px 18px',
      minWidth: 130,
      flex: 1,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function fmtRate(r) {
  if (r == null) return '—';
  return `${(r * 100).toFixed(1)}%`;
}
function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}
function fmtCost(usd) {
  if (usd == null) return '—';
  if (usd === 0) return '$0.00';
  if (usd < 0.001) return `$${usd.toFixed(6)}`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

// ─── AlertRuleForm ────────────────────────────────────────────────────────────

const METRIC_OPTIONS = [
  { value: 'failure_rate',     label: 'Failure Rate',         hint: '0–1, e.g. 0.2 = 20%' },
  { value: 'avg_duration_ms',  label: 'Avg Duration',         hint: 'milliseconds, e.g. 30000' },
  { value: 'p95_duration_ms',  label: 'P95 Duration',         hint: 'milliseconds, e.g. 60000' },
  { value: 'ai_cost_usd_30d',  label: 'AI Spend (30d USD)',   hint: 'USD, e.g. 5.00 = alert when 30-day AI spend > $5' },
];

const WINDOW_OPTIONS = [
  { value: 60,    label: '1 hour' },
  { value: 360,   label: '6 hours' },
  { value: 1440,  label: '24 hours' },
  { value: 10080, label: '7 days' },
  { value: 43200, label: '30 days' },
];

const CHANNEL_OPTIONS = [
  { value: 'email',   label: 'Email' },
  { value: 'slack',   label: 'Slack Webhook' },
  { value: 'webhook', label: 'HTTP Webhook' },
];

const BLANK_FORM = {
  name: '',
  metric: 'failure_rate',
  threshold: '',
  window_minutes: 1440,
  channel: 'email',
  channel_config: {},
  is_enabled: true,
};

function AlertRuleForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || BLANK_FORM);
  const [ccStr, setCcStr] = useState(() => {
    const cc = (initial || BLANK_FORM).channel_config || {};
    return JSON.stringify(cc, null, 2);
  });
  const [ccError, setCcError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleChannelChange(ch) {
    set('channel', ch);
    const defaults = {
      email:   { email: '' },
      slack:   { webhook_url: '' },
      webhook: { url: '', method: 'POST' },
    };
    const defaultCc = defaults[ch] || {};
    setCcStr(JSON.stringify(defaultCc, null, 2));
    setCcError('');
  }

  function handleSubmit(e) {
    e.preventDefault();
    let cc = {};
    try {
      cc = JSON.parse(ccStr || '{}');
      setCcError('');
    } catch {
      setCcError('Invalid JSON');
      return;
    }
    onSave({ ...form, channel_config: cc, threshold: parseFloat(form.threshold) });
  }

  const inputStyle = {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: 6,
    color: 'var(--text-primary)',
    padding: '6px 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };

  const selectedMetric = METRIC_OPTIONS.find(m => m.value === form.metric);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={labelStyle}>Rule Name</label>
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. High failure rate" />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Metric</label>
          <select style={inputStyle} value={form.metric} onChange={e => set('metric', e.target.value)}>
            {METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Threshold {selectedMetric ? `(${selectedMetric.hint})` : ''}</label>
          <input style={inputStyle} type="number" step="any" value={form.threshold} onChange={e => set('threshold', e.target.value)} required placeholder="0.2" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Evaluation Window</label>
          <select style={inputStyle} value={form.window_minutes} onChange={e => set('window_minutes', parseInt(e.target.value))}>
            {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Alert Channel</label>
          <select style={inputStyle} value={form.channel} onChange={e => handleChannelChange(e.target.value)}>
            {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Channel Config (JSON)</label>
        <textarea
          style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 70, resize: 'vertical' }}
          value={ccStr}
          onChange={e => { setCcStr(e.target.value); setCcError(''); }}
        />
        {ccError && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 2 }}>{ccError}</div>}
        {form.channel === 'email' && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{"{ \"email\": \"you@example.com\" } — leave blank to use owner's email"}</div>}
        {form.channel === 'slack' && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{"{ \"webhook_url\": \"https://hooks.slack.com/...\" }"}</div>}
        {form.channel === 'webhook' && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{"{ \"url\": \"https://...\", \"method\": \"POST\" }"}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" id="enabled-chk" checked={form.is_enabled} onChange={e => set('is_enabled', e.target.checked)} />
        <label htmlFor="enabled-chk" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>Enabled</label>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
        <button type="submit" style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save Rule</button>
      </div>
    </form>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function WorkflowMetrics({ workflowId, workflowName, getAuthHeaders, onClose }) {
  const [window7or30, setWindow7or30] = useState('7d');
  const [metrics, setMetrics]         = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [aiCost, setAiCost]           = useState(null);
  const [rules, setRules]             = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [editRule, setEditRule]       = useState(null); // rule being edited
  const [error, setError]             = useState('');

  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/metrics?window=${window7or30}`, { headers: getAuthHeaders() });
      setMetrics(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setLoadingMetrics(false);
    }
  }, [workflowId, window7or30, getAuthHeaders]);

  const fetchAiCost = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/ai-cost`, { headers: getAuthHeaders() });
      setAiCost(res.data);
    } catch {
      // silently ignore — cost data is optional
    }
  }, [workflowId, getAuthHeaders]);

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/alert-rules`, { headers: getAuthHeaders() });
      setRules(res.data);
    } catch {
      // silently ignore
    } finally {
      setLoadingRules(false);
    }
  }, [workflowId, getAuthHeaders]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);
  useEffect(() => { fetchAiCost(); }, [fetchAiCost]);
  useEffect(() => { fetchRules(); }, [fetchRules]);

  async function saveRule(formData) {
    try {
      if (editRule) {
        await axios.put(`${API_BASE_URL}/alert-rules/${editRule.id}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_BASE_URL}/workflows/${workflowId}/alert-rules`, formData, { headers: getAuthHeaders() });
      }
      setShowForm(false);
      setEditRule(null);
      fetchRules();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save rule');
    }
  }

  async function deleteRule(ruleId) {
    if (!(await window.confirm('Delete this alert rule?'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/alert-rules/${ruleId}`, { headers: getAuthHeaders() });
      fetchRules();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete rule');
    }
  }

  async function toggleRule(rule) {
    try {
      await axios.put(`${API_BASE_URL}/alert-rules/${rule.id}`, { is_enabled: !rule.is_enabled }, { headers: getAuthHeaders() });
      fetchRules();
    } catch {
      // silently ignore
    }
  }

  const m = metrics?.metrics || {};
  const trend = metrics?.trend || [];

  const panelStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: 640,
    height: '100vh',
    background: 'var(--bg-primary)',
    borderLeft: '1px solid var(--border-color)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'inherit',
    color: 'var(--text-primary)',
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Run Metrics & Alerting</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{workflowName}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 18, cursor: 'pointer', padding: '2px 6px' }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {/* Window toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['7d', '30d'].map(w => (
            <button key={w} onClick={() => setWindow7or30(w)} style={{
              background: window7or30 === w ? '#3b82f6' : 'var(--bg-secondary)',
              border: '1px solid ' + (window7or30 === w ? '#3b82f6' : 'var(--border-color)'),
              color: window7or30 === w ? '#fff' : 'var(--text-secondary)',
              borderRadius: 6,
              padding: '4px 14px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: window7or30 === w ? 600 : 400,
            }}>Last {w}</button>
          ))}
          <button onClick={fetchMetrics} style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>↻ Refresh</button>
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {loadingMetrics && <div style={{ color: 'var(--text-tertiary)', fontSize: 13, marginBottom: 12 }}>Loading metrics…</div>}

        {/* Metric cards */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <MetricCard label="Total Runs" value={m.total_runs ?? '—'} />
          <MetricCard label="Success Rate" value={fmtRate(m.success_rate)} sub={`${m.success_count ?? 0} ok / ${m.failure_count ?? 0} fail`} />
          <MetricCard label="Avg Duration" value={fmtMs(m.avg_duration_ms)} />
          <MetricCard label="P95 Duration" value={fmtMs(m.p95_duration_ms)} />
          <MetricCard
            label={`AI Spend (${window7or30})`}
            value={fmtCost(metrics?.ai_cost_usd)}
            sub={aiCost ? `30d total: ${fmtCost(aiCost.total_cost_usd)}` : null}
          />
          {metrics?.sla_max_seconds && (
            <MetricCard
              label="SLA Compliance"
              value={metrics.sla_compliance != null ? fmtRate(metrics.sla_compliance) : '—'}
              sub={metrics.sla_breached_count > 0 ? `${metrics.sla_breached_count} breach${metrics.sla_breached_count > 1 ? 'es' : ''}` : `SLA: ${metrics.sla_max_seconds}s`}
            />
          )}
        </div>

        {/* AI Cost breakdown (shown when there is cost data) */}
        {aiCost && aiCost.total_cost_usd > 0 && (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>AI Spend Breakdown — Last 30 days</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                Prompt tokens: <span style={{ color: 'var(--text-primary)' }}>{aiCost.total_prompt_tokens?.toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                Completion tokens: <span style={{ color: 'var(--text-primary)' }}>{aiCost.total_completion_tokens?.toLocaleString()}</span>
              </div>
            </div>
            {aiCost.by_provider_model?.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: 'var(--text-tertiary)', textAlign: 'left' }}>
                    <th style={{ paddingBottom: 6 }}>Provider / Model</th>
                    <th style={{ paddingBottom: 6, textAlign: 'right' }}>Prompt</th>
                    <th style={{ paddingBottom: 6, textAlign: 'right' }}>Completion</th>
                    <th style={{ paddingBottom: 6, textAlign: 'right' }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {aiCost.by_provider_model.map(row => (
                    <tr key={`${row.provider}/${row.model}`} style={{ borderTop: '1px solid #1e3a5f' }}>
                      <td style={{ padding: '4px 0', color: 'var(--text-primary)' }}>{row.provider} / {row.model}</td>
                      <td style={{ padding: '4px 0', textAlign: 'right', color: 'var(--text-secondary)' }}>{row.prompt_tokens?.toLocaleString()}</td>
                      <td style={{ padding: '4px 0', textAlign: 'right', color: 'var(--text-secondary)' }}>{row.completion_tokens?.toLocaleString()}</td>
                      <td style={{ padding: '4px 0', textAlign: 'right', color: '#22c55e', fontWeight: 600 }}>{fmtCost(row.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Trend chart */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', gap: 14 }}>
            <span>Run Trend — {window7or30 === '7d' ? 'Last 7 days' : 'Last 30 days'}</span>
            <span style={{ color: '#22c55e' }}>■ Success</span>
            <span style={{ color: '#ef4444' }}>■ Failed</span>
          </div>
          {trend.length > 0
            ? <TrendChart trend={trend} />
            : <div style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>No run data in this window</div>
          }
        </div>

        {/* Alert Rules section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Alert Rules</div>
          {!showForm && (
            <button onClick={() => { setShowForm(true); setEditRule(null); }} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ New Rule</button>
          )}
        </div>

        {(showForm || editRule) && (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>{editRule ? 'Edit Alert Rule' : 'New Alert Rule'}</div>
            <AlertRuleForm
              initial={editRule || BLANK_FORM}
              onSave={saveRule}
              onCancel={() => { setShowForm(false); setEditRule(null); }}
            />
          </div>
        )}

        {loadingRules && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Loading rules…</div>}

        {!loadingRules && rules.length === 0 && !showForm && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: 12, padding: '12px 0' }}>
            No alert rules configured. Create one to get notified when metrics breach a threshold.
          </div>
        )}

        {rules.map(rule => (
          <div key={rule.id} style={{
            background: 'var(--bg-secondary)',
            border: '1px solid ' + (rule.is_enabled ? 'var(--border-color)' : 'var(--bg-secondary)'),
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 8,
            opacity: rule.is_enabled ? 1 : 0.55,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{rule.name}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => toggleRule(rule)} title={rule.is_enabled ? 'Disable' : 'Enable'} style={{ background: 'none', border: '1px solid var(--border-color)', color: rule.is_enabled ? '#22c55e' : 'var(--text-tertiary)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>
                  {rule.is_enabled ? 'ON' : 'OFF'}
                </button>
                <button onClick={() => { setEditRule(rule); setShowForm(false); }} style={{ background: 'none', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Edit</button>
                <button onClick={() => deleteRule(rule.id)} style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Delete</button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>Metric: <span style={{ color: 'var(--text-primary)' }}>{rule.metric}</span></span>
              <span>Threshold: <span style={{ color: 'var(--text-primary)' }}>{rule.threshold}</span></span>
              <span>Window: <span style={{ color: 'var(--text-primary)' }}>{WINDOW_OPTIONS.find(o => o.value === rule.window_minutes)?.label || `${rule.window_minutes}m`}</span></span>
              <span>Channel: <span style={{ color: 'var(--text-primary)' }}>{rule.channel}</span></span>
            </div>
            {rule.last_triggered_at && (
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Last triggered: {formatLocalDateTime(rule.last_triggered_at)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
